import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  ListItemButton,
  Typography,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getOnboardingSettings, updateOnboardingSettings } from 'src/shared/services/regSettingService';
import { RegSettingDetail } from 'src/settings/models/regSetting';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import { OnboardingStepOrganization } from './steps/onboardingStepOrganization';
import { OnboardingStepProfiles } from './steps/onboardingStepProfiles';
import { OnboardingStepForms } from './steps/onboardingStepForms';
import { OnboardingStepImport } from './steps/onboardingStepImport';
import { OnboardingStepMemberFee } from './steps/onboardingStepMemberFee';
import { OnboardingStepInvoiceTemplates } from './steps/onboardingStepInvoiceTemplates';
import { OnboardingStepPaymentMethods } from './steps/onboardingStepPaymentMethods';
import { OnboardingStepWebForm } from './steps/onboardingStepWebForm';
import { OnboardingStepPaidEmail } from './steps/onboardingStepPaidEmail';
import { OnboardingStepEmbed } from './steps/onboardingStepEmbed';

export interface OnboardingCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  steps: OnboardingStep[];
}

export interface OnboardingStep {
  id: string;
  label: string;
  completed: boolean;
  component: React.ReactNode;
}

type OnboardingWizardProps = {
  open: boolean;
  onClose: () => void;
};

export const OnboardingWizard = ({ open, onClose }: OnboardingWizardProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const { currentUser } = useGlobals();
  const navigate = useNavigate();

  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [onboardingSettings, setOnboardingSettings] = useState<RegSettingDetail[]>([]);
  const [showChecklist, setShowChecklist] = useState(false);
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [navigateTarget, setNavigateTarget] = useState<string | null>(null);
  const [showHighlight, setShowHighlight] = useState(false);
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ladda sparade steg från databasen
  useEffect(() => {
    if (!open) return;
    const loadSettings = async () => {
      try {
        const res = await getOnboardingSettings();
        const settings = res.data;
        setOnboardingSettings(settings);
        const stepsSetting = settings.find((s) => s.keyName === 'onboardingCompletedSteps');
        if (stepsSetting?.varchar) {
          const saved = stepsSetting.varchar.split(',').filter(Boolean);
          setCompletedSteps(new Set(saved));
          if (saved.length > 0) setShowChecklist(true);
        }
      } catch (e) {
        ConsoleHelper.log(e);
      }
    };
    loadSettings();
  }, [open]);

  // Spara steg till databasen vid ändring
  const saveCompletedSteps = async (steps: Set<string>) => {
    try {
      const stepsSetting = onboardingSettings.find((s) => s.keyName === 'onboardingCompletedSteps');
      const stepsValue = Array.from(steps).join(',');
      const update: RegSettingDetail = stepsSetting
        ? { ...stepsSetting, varchar: stepsValue }
        : {
            settings_id: 0,
            product_id: 0,
            category: 'onboarding',
            keyName: 'onboardingCompletedSteps',
            field: 'varchar',
            varchar: stepsValue,
            integer: 0,
            smallint: 0,
            bit: false,
            text: '',
          };
      await updateOnboardingSettings([update]);
      // Meddela headern att uppdatera badge
      window.dispatchEvent(new CustomEvent('onboarding-steps-changed', { detail: steps.size }));
      // Uppdatera lokalt state om ny post skapades
      if (!stepsSetting) {
        const res = await getOnboardingSettings();
        setOnboardingSettings(res.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    }
  };

  useEffect(() => {
    if (showHighlight || showCloseConfirm) {
      const btn = document.getElementById('onboarding-button');
      if (btn) {
        setHighlightRect(btn.getBoundingClientRect());
      }
    }
    if (showHighlight && !showCloseConfirm) {
      highlightTimerRef.current = setTimeout(() => {
        setShowHighlight(false);
        setHighlightRect(null);
      }, 4000);
    }
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, [showHighlight, showCloseConfirm]);

  const isComplete = (stepId: string) => completedSteps.has(stepId);

  const categories: OnboardingCategory[] = [
    {
      id: 'organization',
      label: t('onboarding:categories.organization'),
      icon: <ApartmentIcon />,
      steps: [
        {
          id: 'logo',
          label: t('onboarding:steps.logo'),
          completed: isComplete('logo'),
          component: <OnboardingStepOrganization onLogoChange={(has: boolean) => setHasLogo(has)} />,
        },
      ],
    },
    {
      id: 'memberRegistry',
      label: t('onboarding:categories.memberRegistry'),
      icon: <PeopleIcon />,
      steps: [
        {
          id: 'profiles',
          label: t('onboarding:steps.profiles'),
          completed: isComplete('profiles'),
          component: (
            <OnboardingStepProfiles
              onNavigateAway={() => {
                setNavigateTarget(RoutesEnum.MemberRegistrySettingsProfiles);
                setShowCloseConfirm(true);
              }}
            />
          ),
        },
        {
          id: 'forms',
          label: t('onboarding:steps.forms'),
          completed: isComplete('forms'),
          component: (
            <OnboardingStepForms
              onNavigateAway={() => {
                setNavigateTarget(RoutesEnum.MemberRegistrySettingsForms);
                setShowCloseConfirm(true);
              }}
            />
          ),
        },
        {
          id: 'import',
          label: t('onboarding:steps.import'),
          completed: isComplete('import'),
          component: (
            <OnboardingStepImport
              onNavigateAway={() => {
                setNavigateTarget('/membersimport/upload');
                setShowCloseConfirm(true);
              }}
            />
          ),
        },
      ],
    },
    {
      id: 'invoicing',
      label: t('onboarding:categories.invoicing'),
      icon: <ReceiptIcon />,
      steps: [
        {
          id: 'memberFee',
          label: t('onboarding:steps.memberFee'),
          completed: isComplete('memberFee'),
          component: (
            <OnboardingStepMemberFee
              onNavigateAway={() => {
                setNavigateTarget(`${RoutesEnum.OrganizationMyAssociation}/avgift`);
                setShowCloseConfirm(true);
              }}
            />
          ),
        },
      ],
    },
    {
      id: 'webForms',
      label: t('onboarding:categories.webForms'),
      icon: <DynamicFormIcon />,
      steps: [
        {
          id: 'webForm',
          label: t('onboarding:steps.webForm'),
          completed: isComplete('webForm'),
          component: (
            <OnboardingStepWebForm
              onNavigateAway={() => {
                setNavigateTarget(RoutesEnum.MemberRegistrySettingsWebForms);
                setShowCloseConfirm(true);
              }}
            />
          ),
        },
      ],
    },
    {
      id: 'invoiceAndPayment',
      label: t('onboarding:categories.invoiceAndPayment'),
      icon: <ReceiptIcon />,
      steps: [
        {
          id: 'paymentMethods',
          label: t('onboarding:steps.paymentMethods'),
          completed: isComplete('paymentMethods'),
          component: (
            <OnboardingStepPaymentMethods
              onNavigateAway={() => {
                setNavigateTarget(RoutesEnum.AdminInvoiceGroupSettings);
                setShowCloseConfirm(true);
              }}
            />
          ),
        },
        {
          id: 'invoiceTemplates',
          label: t('onboarding:steps.invoiceTemplates'),
          completed: isComplete('invoiceTemplates'),
          component: (
            <OnboardingStepInvoiceTemplates
              onNavigateAway={() => {
                setNavigateTarget('/settings/invoicegroup/invoice-templates');
                setShowCloseConfirm(true);
              }}
            />
          ),
        },
      ],
    },
    {
      id: 'finalize',
      label: t('onboarding:categories.finalize'),
      icon: <RocketLaunchIcon />,
      steps: [
        {
          id: 'embed',
          label: t('onboarding:steps.embed'),
          completed: isComplete('embed'),
          component: (
            <OnboardingStepEmbed
              onNavigateAway={() => {
                setNavigateTarget(RoutesEnum.MemberRegistrySettingsWebForms);
                setShowCloseConfirm(true);
              }}
            />
          ),
        },
        {
          id: 'paidEmail',
          label: t('onboarding:steps.paidEmail'),
          completed: isComplete('paidEmail'),
          component: (
            <OnboardingStepPaidEmail
              onNavigateAway={() => {
                setNavigateTarget(RoutesEnum.MemberRegistrySettingsGeneral);
                setShowCloseConfirm(true);
              }}
            />
          ),
        },
      ],
    },
  ];

  const markComplete = (stepId: string) => {
    setCompletedSteps((prev) => {
      const updated = new Set(prev).add(stepId);
      saveCompletedSteps(updated);
      setTimeout(() => {
        const allCurrentSteps = categories.flatMap((c) => c.steps);
        const currentIndex = allCurrentSteps.findIndex((s) => s.id === stepId);
        const nextIncomplete = allCurrentSteps.find((s, i) => i > currentIndex && !updated.has(s.id));
        if (nextIncomplete) {
          setActiveStepId(nextIncomplete.id);
        } else {
          setActiveStepId(null);
        }
      }, 0);
      return updated;
    });
  };

  const allSteps = categories.flatMap((c) => c.steps);
  const totalSteps = allSteps.length;
  const completedCount = allSteps.filter((s) => s.completed).length;
  const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  const activeStep = activeStepId ? allSteps.find((s) => s.id === activeStepId) : null;

  const getNextIncompleteStep = (): string | null => {
    const incomplete = allSteps.find((s) => !s.completed);
    return incomplete?.id ?? null;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setShowCloseConfirm(true)}
        maxWidth='lg'
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh', maxHeight: '90vh' } }}
      >
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {!showChecklist && !activeStep ? (
            /* Välkomstsida */
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <IconButton onClick={() => setShowCloseConfirm(true)} size='small'>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Grid container sx={{ flex: 1, alignItems: 'center' }}>
                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <Box
                    component='img'
                    src='/images/onboarding_hero.png'
                    alt='Föreningssupport'
                    sx={{ width: '100%', maxWidth: 420, borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ p: 4 }}>
                  <Typography variant='h4' fontWeight={700} sx={{ mb: 2 }}>
                    {t('onboarding:checklist.almostReady')}
                  </Typography>
                  <Typography variant='subtitle1' sx={{ lineHeight: 1.8, mb: 2 }}>
                    {t('onboarding:checklist.intro')}{' '}
                    <a href='https://hjalp.foreningssupport.se' target='_blank' rel='noopener noreferrer' style={{ color: '#1976d2' }}>
                      {t('onboarding:checklist.helpPages')}
                    </a>{' '}
                    {t('onboarding:checklist.introSuffix')}
                  </Typography>
                  <Typography variant='subtitle1' sx={{ fontStyle: 'italic', mb: 4 }}>
                    {t('onboarding:checklist.regards')}
                  </Typography>
                  <Button variant='contained' size='large' onClick={() => setShowChecklist(true)}>
                    {t('onboarding:getStarted')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ) : (
            /* Checklista / aktivt steg */
            <>
              {/* Header */}
              <Box sx={{ p: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {activeStep && (
                      <IconButton onClick={() => setActiveStepId(null)} size='small'>
                        <ArrowBackIcon />
                      </IconButton>
                    )}
                    <Typography variant='h5' fontWeight={600}>
                      {activeStep ? activeStep.label : t('onboarding:title')}
                    </Typography>
                  </Box>
                  <IconButton onClick={() => setShowCloseConfirm(true)} size='small'>
                    <CloseIcon />
                  </IconButton>
                </Box>

                {/* Progressbar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LinearProgress
                    variant='determinate'
                    value={progressPercent}
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': { borderRadius: 4 },
                    }}
                  />
                  <Typography variant='body2' fontWeight={600} color='text.secondary' sx={{ minWidth: 45 }}>
                    {progressPercent}%
                  </Typography>
                </Box>
              </Box>

              <Divider />

              {/* Innehåll */}
              <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#f1f5f9' }}>
                {activeStep ? (
                  <Box sx={{ p: 3 }}>{activeStep.component}</Box>
                ) : (
                  <Box sx={{ p: 3, bgcolor: '#fff' }}>
                    {(() => {
                      let stepNumber = 0;
                      return categories.map((category) => {
                        const categoryComplete = category.steps.every((s) => completedSteps.has(s.id));
                        return (
                          <Grid container key={category.id} sx={{ mb: 1, borderBottom: 1, borderColor: 'grey.200', pb: 1 }}>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pt: 1, pl: 2 }}>
                              <Box sx={{ color: categoryComplete ? 'success.main' : 'text.secondary' }}>{category.icon}</Box>
                              <Typography variant='body2' fontWeight={500} color='text.secondary'>
                                {category.label}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={9}>
                              {category.steps.map((step) => {
                                stepNumber += 1;
                                const num = stepNumber;
                                return (
                                  <ListItemButton
                                    key={step.id}
                                    onClick={() => setActiveStepId(step.id)}
                                    sx={{
                                      borderRadius: 1,
                                      my: 0.25,
                                      py: 0.75,
                                      bgcolor: step.completed ? 'success.50' : 'transparent',
                                      '&:hover': { bgcolor: step.completed ? 'success.100' : 'action.hover' },
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                      {step.completed ? (
                                        <CheckCircleIcon color='success' sx={{ fontSize: 22 }} />
                                      ) : (
                                        <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: 'grey.400' }} />
                                      )}
                                      <Typography
                                        variant='subtitle1'
                                        fontWeight={500}
                                        sx={{
                                          flex: 1,
                                          textDecoration: step.completed ? 'line-through' : 'none',
                                          color: step.completed ? 'text.secondary' : 'text.primary',
                                        }}
                                      >
                                        {num}. {step.label}
                                      </Typography>
                                      <ChevronRightIcon sx={{ color: 'grey.400', fontSize: 24 }} />
                                    </Box>
                                  </ListItemButton>
                                );
                              })}
                            </Grid>
                          </Grid>
                        );
                      });
                    })()}
                  </Box>
                )}
              </Box>

              {/* Footer */}
              <Divider />
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={activeStep ? () => setActiveStepId(null) : () => setShowCloseConfirm(true)} color='inherit'>
                  {activeStep ? t('onboarding:backToChecklist') : t('onboarding:continueLayer')}
                </Button>
                {activeStep && (
                  <Button
                    variant='contained'
                    onClick={() => markComplete(activeStep.id)}
                    disabled={
                      activeStep.id === 'logo' && !hasLogo && (currentUser?.associationCount ?? 0) <= 1 && (currentUser?.districtCount ?? 0) === 0
                    }
                  >
                    {t('onboarding:markComplete')}
                  </Button>
                )}
                {!activeStep && completedCount < totalSteps && (
                  <Button variant='contained' onClick={() => setActiveStepId(getNextIncompleteStep())}>
                    {t('onboarding:continueSetup')}
                  </Button>
                )}
                {!activeStep && completedCount >= totalSteps && (
                  <Button variant='contained' color='success' onClick={() => setShowCompleteConfirm(true)}>
                    {t('onboarding:complete')}
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bekräftelsedialog vid stängning */}
      <Dialog
        open={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        maxWidth='sm'
        fullWidth
        sx={{ zIndex: 1400 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.3)' } } }}
      >
        <DialogTitle>{t('onboarding:closeConfirm.title')}</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            {t('onboarding:closeConfirm.description')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', bgcolor: 'grey.100', borderRadius: 1, p: 1.5 }}>
            <Typography variant='body2' color='text.secondary'>
              {t('onboarding:navigateAwayHint')}
            </Typography>
            <Button
              variant='outlined'
              size='small'
              startIcon={<RocketLaunchIcon sx={{ fontSize: 14 }} />}
              disabled
              sx={{ fontSize: '0.7rem', py: 0, minHeight: 24, whiteSpace: 'nowrap' }}
            >
              {t('common:headerToolbar.onboarding')}
            </Button>
            <Typography variant='body2' color='text.secondary'>
              {t('onboarding:navigateAwayHintSuffix')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseConfirm(false)} color='inherit'>
            {t('onboarding:closeConfirm.cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              setShowCloseConfirm(false);
              setShowHighlight(true);
              onClose();
              if (navigateTarget) {
                navigate(navigateTarget);
                setNavigateTarget(null);
              }
            }}
          >
            {t('onboarding:closeConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Bekräftelsedialog vid slutförande */}
      <Dialog
        open={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        maxWidth='sm'
        fullWidth
        sx={{ zIndex: 1400 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.3)' } } }}
      >
        <DialogTitle>{t('onboarding:completeConfirm.title')}</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>{t('onboarding:completeConfirm.description')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCompleteConfirm(false)} color='inherit'>
            {t('onboarding:closeConfirm.cancel')}
          </Button>
          <Button
            variant='contained'
            color='success'
            onClick={async () => {
              // Stäng av onboarding
              const enabledSetting = onboardingSettings.find((s) => s.keyName === 'onboardingEnabled');
              if (enabledSetting) {
                await updateOnboardingSettings([{ ...enabledSetting, bit: false }]);
              }
              window.dispatchEvent(new CustomEvent('onboarding-disabled'));
              setShowCompleteConfirm(false);
              onClose();
            }}
          >
            {t('onboarding:completeConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Highlight-overlay på knappen i headern */}
      {(showHighlight || showCloseConfirm) &&
        highlightRect &&
        ReactDOM.createPortal(
          <Box
            onClick={
              showCloseConfirm
                ? undefined
                : () => {
                    setShowHighlight(false);
                    setHighlightRect(null);
                  }
            }
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1401,
              pointerEvents: showCloseConfirm ? 'none' : 'auto',
              cursor: showCloseConfirm ? 'default' : 'pointer',
            }}
          >
            {/* Kopia av knappen som visas ovanpå */}
            <Box
              sx={{
                position: 'absolute',
                top: highlightRect.top,
                left: highlightRect.left,
                width: highlightRect.width,
                height: highlightRect.height,
                pointerEvents: 'none',
              }}
            >
              <Button
                variant='outlined'
                size='small'
                startIcon={<RocketLaunchIcon />}
                sx={{ textTransform: 'none', borderRadius: 2, width: '100%', height: '100%', pointerEvents: 'none', bgcolor: '#fff' }}
              >
                {t('common:headerToolbar.onboarding')}
              </Button>
            </Box>
            {/* Pulsande röd ram runt knappen */}
            <Box
              sx={{
                position: 'absolute',
                top: highlightRect.top - 4,
                left: highlightRect.left - 4,
                width: highlightRect.width + 8,
                height: highlightRect.height + 8,
                border: '3px solid #f44336',
                borderRadius: 2,
                animation: 'onboarding-pulse 1s ease-in-out infinite',
                '@keyframes onboarding-pulse': {
                  '0%, 100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)' },
                  '50%': { boxShadow: '0 0 0 8px rgba(244, 67, 54, 0)' },
                },
              }}
            />
            {/* Pil nedåt */}
            <Box
              sx={{
                position: 'absolute',
                top: highlightRect.bottom + 8,
                left: highlightRect.left + highlightRect.width / 2 - 100,
                bgcolor: '#f44336',
                color: '#fff',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -6,
                  left: 'calc(50% - 6px)',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderBottom: '6px solid #f44336',
                },
              }}
            >
              {t('onboarding:closeConfirm.clickHere')}
            </Box>
          </Box>,
          document.body,
        )}
    </>
  );
};
